import React from "react";
import { createViewAction, formatDateTime } from "../../utils/helpers";
import { FormActions } from "../../components/common/FormActions";
import { URL_FRAUD_AUDIT_LOGS } from "../../utils/urls";

const url = URL_FRAUD_AUDIT_LOGS;

const flattenObject = (obj: any, prefix = ""): Record<string, any> => {
  if (!obj || typeof obj !== "object") return {};

  return Object.keys(obj).reduce(
    (acc, key) => {
      const value = obj[key];
      const newKey = prefix ? `${prefix}.${key}` : key;

      if (value && typeof value === "object" && !Array.isArray(value)) {
        // Recursively flatten nested objects
        Object.assign(acc, flattenObject(value, newKey));
      } else if (Array.isArray(value)) {
        // Convert arrays to string
        acc[newKey] = JSON.stringify(value);
      } else {
        // Primitive values
        acc[newKey] =
          value !== null && value !== undefined ? String(value) : "N/A";
      }
      return acc;
    },
    {} as Record<string, any>,
  );
};

const useFraudAuditLogs = () => {
  const tableColumns = () => [
    { id: "log_id", header: "Log ID", filter: {} },
    { id: "action_type", header: "Action Type", filter: { type: "text" } },
    { id: "user", header: "User", filter: {} },
    { id: "ip_address", header: "IP Address", filter: {} },
    { id: "user_agent", header: "User Agent", filter: {} },
    {
      id: "affected_entity_type",
      header: "Affected Entity Type",
      filter: { type: "text" },
    },
    { id: "affected_entity_id", header: "Affected Entity ID", filter: {} },
    { id: "old_value", header: "Old Value", filter: {} },
    { id: "new_value", header: "New Value", filter: {} },
    { id: "timestamp", header: "Timestamp", filter: { type: "date" } },
    { id: "actions", header: "Actions" },
  ];

  const tableData = (data: any) => [
    ...data?.map((item: any) => {
      // Create a more readable summary for table display
      const formatValueForTable = (value: any) => {
        if (!value) return "N/A";
        if (typeof value !== "object") return String(value);

        // For objects, show key count or a brief summary
        const keys = Object.keys(value);
        if (keys.length === 0) return "Empty";
        if (keys.length <= 3) {
          // Show brief summary for small objects
          return keys.map((k) => `${k}: ${value[k]}`).join(", ");
        }
        return `${keys.length} properties`;
      };

      return {
        ...item,
        timestamp: formatDateTime(item?.timestamp),
        old_value: formatValueForTable(item?.old_value),
        new_value: formatValueForTable(item?.new_value),
        user: item?.user || "System",
        actions: <FormActions actions={[createViewAction(url, item?.id)]} />,
      };
    }),
  ];

  const viewData = (item: any) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: {
              message: "No data available",
            },
          },
        ],
      };
    }
    return {
      sections: [
        {
          title: "Audit Log Details",
          fields: {
            log_id: item?.log_id,
            action_type: item?.action_type,
            description: item?.description,
            ip_address: item?.ip_address,
            user_agent: item?.user_agent,
            affected_entity_type: item?.affected_entity_type,
            affected_entity_id: item?.affected_entity_id,
            timestamp: formatDateTime(item?.timestamp),
            session_id: item?.session_id || "N/A",
          },
        },
        {
          title: "Old Value",
          fields: item?.old_value
            ? flattenObject(item.old_value)
            : { message: "No old value" },
        },
        {
          title: "New Value",
          fields: item?.new_value
            ? flattenObject(item.new_value)
            : { message: "No new value" },
        },
        {
          title: "Metadata",
          fields: {
            reason: item?.metadata?.reason || "N/A",
            failed_count: item?.metadata?.failed_count || "N/A",
            auto_action: item?.metadata?.auto_action || "N/A",
            version: item?.metadata?.version || "N/A",
          },
        },
        {
          title: "Created By",
          fields: {
            first_name: item?.created_by?.first_name || "System",
            last_name: item?.created_by?.last_name || "",
            phone_number: item?.created_by?.phone_number || "N/A",
            role: item?.created_by?.role || "N/A",
          },
        },
      ],
    };
  };

  return {
    tableColumns,
    tableData,
    viewData,
  };
};

export default useFraudAuditLogs;
